import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdminAuthStore } from "@/store/admin-auth-store";
import { useHydrated } from "@/hooks/useHydrated";
import { Loader2 } from "lucide-react";

/**
 * Route guard for admin panel. Redirects to /login if unauthenticated.
 * Waits for Zustand hydration before deciding to avoid false redirect on reload.
 */
export default function AdminGuard({ children }: { children: React.ReactNode }) {
  const hydrated = useHydrated();
  const isAuthenticated = useAdminAuthStore((s) => s.isAuthenticated);
  const admin = useAdminAuthStore((s) => s.admin);
  const clearAuth = useAdminAuthStore((s) => s.clearAuth);
  const location = useLocation();

  // Stale session: isAuthenticated=true nhưng admin=null / thiếu role.
  // Clear trong effect để không setState trong render.
  const stale = hydrated && isAuthenticated && (!admin || !admin.role);
  useEffect(() => {
    if (stale) clearAuth();
  }, [stale, clearAuth]);

  if (!hydrated) {
    return (
      <div className="flex h-screen items-center justify-center bg-s0">
        <Loader2 className="h-6 w-6 animate-spin text-t2" />
      </div>
    );
  }

  if (!isAuthenticated || !admin || !admin.role) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}
